import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Disclosure } from "@headlessui/react";
import {
  ChevronDownIcon,
  ExclamationTriangleIcon,
  PencilSquareIcon,
  XMarkIcon,
} from "@heroicons/react/24/solid";
import useApiToken from "../../../hooks/useApiToken";
import useClickOutside from "../../../hooks/useClickOutside";
import { Spinner } from "../../Shared/Spinner";
import StarRatingDisplay from "./StarRatingDisplay";
import ScoreFormulaInfo from "./ScoreFormulaInfo";
import InterviewScorecard from "./InterviewScorecard";
import {
  GetStageEvaluations,
  RECOMMENDATION_LABELS,
  RECOMMENDATION_STYLES,
  StageEvaluationSummary,
} from "../../../api/interviewEvaluation.api";

type Props = {
  candidateId: string;
  roleId: string;
  roleStatusId: string;
  stageName: string;
  candidateName?: string;
  onClose: () => void;
  /** Fired after an update from inside this modal, so the badge/grade on the page
   *  behind it (ScoreGrades) can refresh live. */
  onSubmitted?: () => void;
};

/**
 * The one place the structured evaluation lives once it exists: shows the summary by
 * default (every submitted evaluation's per-competency levels and evidence, the
 * combined result, any gate breach), with an "Update" action that swaps in the same
 * editable scorecard used to create it in the first place, then swaps back to the
 * summary once that's submitted. Opened from the single merged score/scorecard badge
 * in ScoreGrades - there's no separate "Scorecard" button once a score exists.
 */
export default function StructuredEvaluationDetailModal({
  candidateId,
  roleId,
  roleStatusId,
  stageName,
  candidateName,
  onClose,
  onSubmitted,
}: Props) {
  const token = useApiToken();
  const modalRef = useRef(null);
  useClickOutside(modalRef, onClose);

  const [mode, setMode] = useState<"summary" | "edit">("summary");
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState<StageEvaluationSummary | null>(null);

  const loadSummary = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const result = await GetStageEvaluations(candidateId, roleId, roleStatusId, token);
      setSummary(result);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, candidateId, roleId, roleStatusId]);

  const handleUpdated = async () => {
    // Back to the summary, refreshed, once the edit is submitted - not left sitting on
    // the form that was just used.
    await loadSummary();
    setMode("summary");
    onSubmitted?.();
  };

  const modalContent = (
    <div className="fixed inset-0 flex justify-center z-50 h-screen backdrop-blur-sm">
      <div
        className="relative p-2 shadow-lg md:w-[70%] w-[95%] max-h-[85%] overflow-y-auto dark:bg-gray-800 bg-gray-50 top-14 rounded-md border border-gray-300 dark:border-gray-600"
        ref={modalRef}
      >
        <div className="mx-auto px-4 pt-0">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md mb-4 flex items-center justify-between p-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {mode === "edit" ? "Update evaluation" : "Structured evaluation"}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">{stageName}</p>
            </div>
            <div className="flex items-center gap-1">
              {mode === "summary" && !loading && summary && summary.evaluations.length > 0 && (
                <button
                  type="button"
                  onClick={() => setMode("edit")}
                  className="inline-flex items-center gap-1.5 rounded-md border border-blue-200 dark:border-blue-800 px-3 py-1.5 text-xs font-semibold text-entntblue hover:bg-blue-50 dark:hover:bg-blue-900/30"
                >
                  <PencilSquareIcon className="h-4 w-4" />
                  Update
                </button>
              )}
              <button
                onClick={onClose}
                className="p-2 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
          </div>

          {mode === "edit" ? (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4">
              <InterviewScorecard
                candidateId={candidateId}
                roleId={roleId}
                roleStatusId={roleStatusId}
                stageName={stageName}
                candidateName={candidateName}
                onSubmitted={handleUpdated}
              />
            </div>
          ) : loading ? (
            <div className="py-10">
              <Spinner height="h-8" width="w-8" />
            </div>
          ) : !summary || summary.evaluations.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400 py-6 text-center">
              No structured evaluation is visible yet for this stage.
            </p>
          ) : (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md">
              <div className="flex flex-wrap items-center gap-4 p-5 border-b border-gray-100 dark:border-gray-700">
                {summary.combinedRecommendation && (
                  <span
                    className={`px-3 py-1 rounded-full text-sm font-bold ${
                      RECOMMENDATION_STYLES[summary.combinedRecommendation]
                    }`}
                  >
                    {RECOMMENDATION_LABELS[summary.combinedRecommendation]}
                  </span>
                )}

                <ScoreFormulaInfo showCombinedRule={summary.submittedCount > 1} />

                {summary.combinedNormalisedScore != null && (
                  <span className="text-lg font-bold text-gray-800 dark:text-gray-100">
                    {summary.combinedNormalisedScore}
                    <span className="text-sm font-normal text-gray-400">/100</span>
                  </span>
                )}

                <span className="text-xs text-gray-400 ml-auto">
                  {summary.submittedCount} interviewer
                  {summary.submittedCount === 1 ? "" : "s"}
                </span>

                {summary.gateBreached && (
                  <span
                    className="flex items-center gap-1 text-xs text-orange-600 dark:text-orange-400"
                    title={summary.gateBreachReasons.join(" ")}
                  >
                    <ExclamationTriangleIcon className="h-4 w-4" />
                    below minimum
                  </span>
                )}
              </div>

              {summary.gateBreachReasons.length > 0 && (
                <ul className="px-4 pb-3 space-y-0.5">
                  {summary.gateBreachReasons.map((r, i) => (
                    <li key={i} className="text-xs text-orange-600 dark:text-orange-400">
                      {r}
                    </li>
                  ))}
                </ul>
              )}

              {summary.evaluations.map((evaluation) => (
                <Disclosure key={evaluation.id} defaultOpen={summary.evaluations.length === 1}>
                  {({ open }) => (
                    <div className="border-t border-gray-100 dark:border-gray-700">
                      <Disclosure.Button className="flex w-full items-center gap-2 px-4 py-3 text-left">
                        <ChevronDownIcon
                          className={`h-4 w-4 text-gray-400 transition ${
                            open ? "rotate-180" : ""
                          }`}
                        />
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          {evaluation.evaluatorName ?? evaluation.evaluatorId}
                        </span>
                        {evaluation.recommendation && (
                          <span
                            className={`px-2 py-0.5 rounded-full text-xs ${
                              RECOMMENDATION_STYLES[evaluation.recommendation]
                            }`}
                          >
                            {RECOMMENDATION_LABELS[evaluation.recommendation]}
                          </span>
                        )}
                        <span className="ml-auto text-xs text-gray-400">
                          {evaluation.submittedAt
                            ? new Date(evaluation.submittedAt).toLocaleDateString()
                            : "Draft"}
                        </span>
                      </Disclosure.Button>

                      <Disclosure.Panel className="px-4 pb-4 space-y-3">
                        {evaluation.competencyScores.map((s) => (
                          <div
                            key={s.competencyId}
                            className="rounded-lg border border-gray-100 dark:border-gray-700 bg-gray-50/60 dark:bg-slate-800/40 p-3"
                          >
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                                {s.competencyName}
                              </span>
                              <div className="flex items-center gap-2">
                                <StarRatingDisplay level={s.level} />
                                {s.level !== null && (
                                  <span className="text-[11px] text-gray-400 dark:text-gray-500">
                                    {s.confidence} confidence
                                  </span>
                                )}
                              </div>
                            </div>
                            {(s.evidenceText || s.notObservedReason) && (
                              <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                                {s.evidenceText ?? s.notObservedReason}
                              </p>
                            )}
                          </div>
                        ))}

                        {evaluation.overallComments && (
                          <p className="pt-2 text-xs italic text-gray-500 dark:text-gray-400">
                            {evaluation.overallComments}
                          </p>
                        )}
                      </Disclosure.Panel>
                    </div>
                  )}
                </Disclosure>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.getElementById("root") as HTMLElement);
}
